import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import PageHero from '../components/PageHero';

const TermsPage: React.FC = () => {
  const { language } = useLanguage();

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white">
      <PageHero 
        title={language === 'en' ? 'Terms of Service' : 'Felhasználási Feltételek'} 
        subtitle={language === 'en' 
          ? 'Please read these terms carefully before using our services'
          : 'Kérjük, figyelmesen olvassa el ezeket a feltételeket szolgáltatásaink használata előtt'
        } 
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-3xl mx-auto">
          <div className="mb-12 text-center">
            <p className="text-md text-gray-500 italic">
              {language === 'en' ? 'Last updated: ' : 'Utoljára frissítve: '} 
              {new Date().toLocaleDateString()}
            </p>
          </div>
          
          <div className="prose prose-indigo prose-lg text-gray-500 max-w-none">
            {language === 'en' ? (
              <>
                <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
                  <h2 className="text-2xl font-bold text-indigo-700 mb-4">1. Agreement to Terms</h2>
                  <p className="mb-0">
                    By accessing our website and services, you agree to be bound by these Terms of Service and 
                    all applicable laws and regulations. If you do not agree with any of these terms, you are 
                    prohibited from using or accessing this site.
                  </p>
                </div>
                
                <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
                  <h2 className="text-2xl font-bold text-indigo-700 mb-4">2. Use License</h2>
                  <p>
                    Permission is granted to temporarily access the materials on YouCRM's website. This is the grant 
                    of a license, not a transfer of title, and under this license you may not:
                  </p>
                  <ul className="list-disc pl-6 mt-4 space-y-2">
                    <li>Modify or copy the materials</li>
                    <li>Attempt to decompile or reverse engineer any software contained on YouCRM's website</li>
                    <li>Remove any copyright or other proprietary notations from the materials</li>
                    <li>Transfer the materials to another person or "mirror" the materials on any other server</li>
                  </ul>
                </div>
                
                <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
                  <h2 className="text-2xl font-bold text-indigo-700 mb-4">3. Service Description</h2>
                  <p className="mb-0">
                    YouCRM provides customer relationship management software as a service. We reserve the right to 
                    modify, suspend or discontinue any part of the service at any time without notice.
                  </p>
                </div>
                
                <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
                  <h2 className="text-2xl font-bold text-indigo-700 mb-4">4. Technical Support</h2>
                  <p className="mb-0">
                    Technical support is provided according to your service agreement. We will make reasonable 
                    efforts to respond to support inquiries in a timely manner.
                  </p>
                </div>
                
                <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
                  <h2 className="text-2xl font-bold text-indigo-700 mb-4">5. Liability</h2>
                  <p className="mb-0">
                    In no event shall YouCRM be liable for any damages arising out of the use or inability to use 
                    the materials on our website or services, even if we have been notified of the possibility of 
                    such damage.
                  </p>
                </div>
                
                <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
                  <h2 className="text-2xl font-bold text-indigo-700 mb-4">6. Governing Law</h2>
                  <p className="mb-0">
                    These terms shall be governed and construed in accordance with the laws applicable in Hungary, 
                    without regard to its conflict of law provisions.
                  </p>
                </div>
                
                <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
                  <h2 className="text-2xl font-bold text-indigo-700 mb-4">7. Changes to Terms</h2>
                  <p className="mb-0">
                    YouCRM reserves the right to modify these terms of service at any time. We will notify clients 
                    of any material changes via email or through our website.
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
                  <h2 className="text-2xl font-bold text-indigo-700 mb-4">1. Feltételek elfogadása</h2>
                  <p className="mb-0">
                    Weboldalunk és szolgáltatásaink elérésével Ön elfogadja, hogy ezeket a Felhasználási Feltételeket 
                    és minden vonatkozó törvényt és előírást betartja. Ha nem ért egyet bármelyik feltétellel, nem 
                    használhatja vagy nem férhet hozzá ehhez a webhelyhez.
                  </p>
                </div>
                
                <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
                  <h2 className="text-2xl font-bold text-indigo-700 mb-4">2. Felhasználási licenc</h2>
                  <p>
                    Engedélyt adunk a YouCRM webhelyén található anyagok ideiglenes hozzáféréséhez. Ez egy licenc 
                    megadása, nem pedig tulajdonjog átruházása, és e licenc alapján Ön nem:
                  </p>
                  <ul className="list-disc pl-6 mt-4 space-y-2">
                    <li>Módosíthatja vagy másolhatja az anyagokat</li>
                    <li>Kísérelheti meg visszafejteni vagy visszatervezni a YouCRM webhelyén található szoftvert</li>
                    <li>Távolíthatja el a szerzői jogi vagy egyéb tulajdonjogi jelzéseket az anyagokról</li>
                    <li>Adhatja át az anyagokat más személynek, vagy "tükrözheti" az anyagokat bármely más szerveren</li>
                  </ul>
                </div>
                
                <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
                  <h2 className="text-2xl font-bold text-indigo-700 mb-4">3. Szolgáltatás leírása</h2>
                  <p className="mb-0">
                    A YouCRM szolgáltatásként nyújt ügyfélkapcsolat-kezelő szoftvert. Fenntartjuk a jogot, hogy a 
                    szolgáltatás bármely részét bármikor előzetes értesítés nélkül módosítsuk, felfüggesszük vagy megszüntessük.
                  </p>
                </div>
                
                <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
                  <h2 className="text-2xl font-bold text-indigo-700 mb-4">4. Technikai támogatás</h2>
                  <p className="mb-0">
                    A technikai támogatást a szolgáltatási megállapodásának megfelelően nyújtjuk. Ésszerű erőfeszítéseket 
                    teszünk a támogatási megkeresésekre való időben történő válaszadásra.
                  </p>
                </div>
                
                <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
                  <h2 className="text-2xl font-bold text-indigo-700 mb-4">5. Felelősség</h2>
                  <p className="mb-0">
                    A YouCRM semmilyen esetben sem vállal felelősséget a webhelyünk vagy szolgáltatásaink használatából 
                    vagy használhatatlanságából eredő károkért, még akkor sem, ha értesítettek bennünket az ilyen kár lehetőségéről.
                  </p>
                </div>
                
                <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
                  <h2 className="text-2xl font-bold text-indigo-700 mb-4">6. Irányadó jog</h2>
                  <p className="mb-0">
                    Ezeket a feltételeket a Magyarországon alkalmazandó jogszabályok szabályozzák és értelmezik, 
                    tekintet nélkül azok kollíziós jogi rendelkezéseire.
                  </p>
                </div>
                
                <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
                  <h2 className="text-2xl font-bold text-indigo-700 mb-4">7. Feltételek módosítása</h2>
                  <p className="mb-0">
                    A YouCRM fenntartja a jogot, hogy ezeket a felhasználási feltételeket bármikor módosítsa. 
                    Az ügyfeleket minden jelentős változásról e-mailben vagy webhelyünkön keresztül értesítjük.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage; 